import { Trans, useLingui } from '@lingui/react/macro';

import { ExceptionalBrandCoverageReason } from '@/api';
import AlertBar from '@/design_system/AlertBar';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import Tooltip from '@/design_system/Tooltip';
import IconAfterSales from '@/icons/AfterSales.svg';
import {
  EXCEPTIONAL_BRAND_COVERAGE_REASONS,
  TEMPORARY_ID_PREFIX,
  useUpdateDefect,
} from '@/models/article';
import { computeDuplicateDefectNumber, useGetDefectName } from '@/models/defectType';
import { type ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

import { getEditedFields } from './getEditedFields';
import { LinkedOriginalRequestReference } from './LinkedOriginalRequestReference';

export const DefectBrandResponsibilityMobile = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  return (
    <Badge
      hasBorder
      color="neutral"
      borderRadius="soft"
      size="large"
      style={
        getEditedFields({ defect, previousDefect }).isBrandResponsibilityEdited
          ? { backgroundColor: 'var(--color-tertiary-100)' }
          : undefined
      }
    >
      <IconAfterSales />
      {(defect ?? previousDefect!).brandResponsibility ? (
        <Trans id="defect-card.brand-res.yes">Brand resp: Yes</Trans>
      ) : (
        <Trans id="defect-card.brand-res.no">Brand resp: No</Trans>
      )}
    </Badge>
  );
};

export const DefectBrandResponsibilityToggle = ({
  defect,
  previousDefect,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
}) => {
  const { t } = useLingui();
  const { article, request, view } = useArticleContext();

  const getDefectName = useGetDefectName();

  const { mutateAsync: updateDefect } = useUpdateDefect({
    requestId: request.id,
    articleId: article.id,
  });

  const isEditable =
    !!defect &&
    view.defects.fields.brandResponsibility.toggle.editable &&
    !defect?.id.startsWith(TEMPORARY_ID_PREFIX);

  const isEdited = getEditedFields({ defect, previousDefect }).isBrandResponsibilityEdited;

  const defectsThatRemoveAllBrandResponsibility = article.snapshot.articleDefects.filter(
    (defect) => defect.removesAllBrandResponsibility
  );

  const wrapperStyles = {
    backgroundColor: isEdited ? 'var(--color-tertiary-100)' : undefined,
  };

  if (defectsThatRemoveAllBrandResponsibility.length && !!defect) {
    const defectNames = defectsThatRemoveAllBrandResponsibility
      .map((defect) => {
        const defectName = getDefectName(defect);
        const duplicateDefectNumber = computeDuplicateDefectNumber(
          defect,
          article.snapshot.articleDefects
        );

        return `"${defectName}${duplicateDefectNumber ? ` #${duplicateDefectNumber}` : ''}"`;
      })
      .join(', ');

    const tooltipLabel =
      defectsThatRemoveAllBrandResponsibility.length > 1
        ? t({
            id: 'defect-card.brand-responsibility.removes-all.many',
            message: `The defects ${defectNames} cancel the brand responsibility for all other defects and disable edition`,
          })
        : defect.removesAllBrandResponsibility
          ? t({
              id: 'defect-card.brand-responsibility.removes-all.single',
              message: `This defect cancels the brand responsibility for all other defects and disables edition`,
            })
          : t({
              id: 'defect-card.brand-responsibility.removes-all.single-other',
              message: `The defect ${defectNames} cancels the brand responsibility for all other defects and disables edition`,
            });

    return (
      <div style={wrapperStyles}>
        <Tooltip content={tooltipLabel}>
          <Button variant="style-less">
            <Toggle
              label={t({
                id: 'defect-card.brand-responsibility.label',
                message: 'Under brand responsibility',
              })}
              alignment="left"
              isSelected={false}
              isDisabled
            />
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <Stack gap="0.5rem" style={wrapperStyles}>
      <Toggle
        label={t({
          id: 'defect-card.brand-responsibility.label',
          message: 'Under brand responsibility',
        })}
        alignment="left"
        isSelected={!!(defect ?? previousDefect)?.brandResponsibility}
        onChange={(value) => {
          if (defect) {
            updateDefect({ defectId: defect.id, body: { brandResponsibility: value } });
          }
        }}
        isDisabled={!isEditable}
      />
      {defect?.exceptionalBrandResponsibilityCoverageRequested && (
        <AlertBar>
          <Trans id="defect-card.brand-responsibility.exceptional-coverage">
            A request for exceptional brand coverage has been made.
          </Trans>
          <ExceptionalCoverageReasonText
            exceptionalBrandResponsibilityCoverageReason={
              defect.exceptionalBrandResponsibilityCoverageReason
            }
            exceptionalBrandResponsibilityCoverageOriginalRequest={
              defect.exceptionalBrandResponsibilityCoverageOriginalRequest
            }
          />
        </AlertBar>
      )}
    </Stack>
  );
};

export const RemovesAllBrandResponsibilityWarning = () => {
  return (
    // eslint-disable-next-line lingui/no-unlocalized-strings
    <AlertBar type="warning" style={{ margin: '0 0.75rem 0.75rem' }}>
      <Trans id="defect-card.removes-all-brand-responsibility-warning">
        The defect cancels the brand responsibility for all other defects
      </Trans>
    </AlertBar>
  );
};

const ExceptionalCoverageReasonText = ({
  exceptionalBrandResponsibilityCoverageReason,
  exceptionalBrandResponsibilityCoverageOriginalRequest,
}: {
  exceptionalBrandResponsibilityCoverageReason: ExceptionalBrandCoverageReason | null;
  exceptionalBrandResponsibilityCoverageOriginalRequest: NonNullable<
    ArticleDefectWithRelations['exceptionalBrandResponsibilityCoverageOriginalRequest']
  > | null;
}) => {
  const { i18n } = useLingui();
  const exceptionalCoverageReason = EXCEPTIONAL_BRAND_COVERAGE_REASONS.find(
    ({ id }) => id === exceptionalBrandResponsibilityCoverageReason
  );

  if (!exceptionalCoverageReason) return null;

  if (
    exceptionalCoverageReason.id === 'covered-by-workshop-warranty' &&
    exceptionalBrandResponsibilityCoverageOriginalRequest
  ) {
    return (
      <>
        {' '}
        ({i18n._(exceptionalCoverageReason.text)}
        {!!exceptionalBrandResponsibilityCoverageOriginalRequest.reference && (
          <>
            {' • '}
            <LinkedOriginalRequestReference
              originalRequest={exceptionalBrandResponsibilityCoverageOriginalRequest}
            />
          </>
        )}
        )
      </>
    );
  }

  return <> ({i18n._(exceptionalCoverageReason.text)})</>;
};
